import { getRecoil, setRecoil } from 'recoil-nexus';
import { cartState } from '../atoms/cartAtom';
import { isStockRequired } from '../utils/stock';

export const CartService = {
  addToCart: (product, quantity = 1, variant = null) => {
    const cart = getRecoil(cartState);
    const newItems = [...(cart.items || [])];
    
    // Determine promo-aware unit price (support multiple API shapes)
    const parseBool = (v) => v === true || v === 'true' || v === 1 || v === '1';
    const rawPrice = parseFloat(product.prixVenteTTC ?? product.prixVenteHt ?? product.price ?? 0) || 0;
    const promoPriceVal = parseFloat(product.prixVentePromo ?? product.PrixVentePromo ?? product.promoPrice ?? 0) || 0;
    const promoActive = parseBool(product.enPromo ?? product.EnPromo) || promoPriceVal > 0;
    let unitPrice = promoActive && promoPriceVal > 0 ? promoPriceVal : rawPrice;

    // Debug log
    console.log('Using product unit price:', unitPrice, { rawPrice, promoPriceVal, promoActive });
    if (isNaN(unitPrice) || !isFinite(unitPrice)) {
      console.error('Failed to extract a valid unit price from product:', { product });
      unitPrice = 0;
    }
    
    // Generate a unique cart item ID
    const cartItemId = `${product.id}${variant ? `-${variant.id}` : ''}`;
    
    // Check if item already exists in cart
    const existingItemIndex = newItems.findIndex(item => 
      item.cartItemId === cartItemId
    );
    
      if (existingItemIndex >= 0) {
        // Update existing item
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + quantity,
          price: unitPrice, // Use the promo-aware unit price
          enPromo: promoActive,
          prixVentePromo: promoPriceVal,
          rawPrice: rawPrice
        };
      } else {
      // Add new item with the product price
      newItems.push({
        cartItemId,
        productId: product.id,
        variantId: variant ? variant.id : null,
        name: product.name + (variant ? ` (${variant.name})` : ''),
          price: unitPrice, // Use the promo-aware unit price
          enPromo: promoActive,
          prixVentePromo: promoPriceVal,
          rawPrice: rawPrice,
        quantity,
        image: variant && variant.imageUrl && variant.imageUrl.length > 0 
          ? variant.imageUrl[0] 
          : (product.imageUrl && product.imageUrl.length > 0 
              ? product.imageUrl[0] 
              : ''),
        variantName: variant ? variant.name : '',
        reference: variant ? variant.reference : product.reference,
        // null = no stock limit in the cart (product sold without stock)
        stock: isStockRequired(product) ? (variant ? variant.stock : product.stock) : null
      });
    }
    
    // Calculate cart totals (safely with Number conversions)
    const count = newItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    
    // Ensure we're calculating with numbers, not strings
    const total = newItems.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);

    console.log('Cart calculation:', {
      items: newItems.map(item => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        total: item.price * item.quantity
      })),
      count,
      total
    });

    // Update cart state
    setRecoil(cartState, {
      ...cart,
      items: newItems,
      count,
      total
    });
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify({
      items: newItems,
      count,
      total
    }));
    
    return { success: true, items: newItems, count, total };
  },
  
  removeFromCart: (cartItemId) => {
    const cart = getRecoil(cartState);
    
    const newItems = (cart.items || []).filter(item => item.cartItemId !== cartItemId);
    
    // Recalculate totals (safely)
    const count = newItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const total = newItems.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);
    
    // Update cart state
    setRecoil(cartState, {
      ...cart,
      items: newItems,
      count,
      total
    });
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify({
      items: newItems,
      count,
      total
    }));
    
    return { success: true, items: newItems, count, total };
  },
  
  updateQuantity: (cartItemId, quantity) => {
    const cart = getRecoil(cartState);
    
    const newItems = (cart.items || []).map(item => 
      item.cartItemId === cartItemId 
        ? { ...item, quantity: Math.max(1, quantity) } 
        : item
    );
    
    // Recalculate totals (safely)
    const count = newItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const total = newItems.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);
    
    // Update cart state
    setRecoil(cartState, {
      ...cart,
      items: newItems,
      count,
      total
    });
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify({
      items: newItems,
      count,
      total
    }));
    
    return { success: true, items: newItems, count, total };
  },
  
  clearCart: () => {
    setRecoil(cartState, {
      items: [],
      count: 0,
      total: 0,
      isOpen: false
    });
    
    localStorage.removeItem('cart');
    
    return { success: true };
  },
  
  toggleCart: () => {
    const cart = getRecoil(cartState);
    setRecoil(cartState, {
      ...cart,
      isOpen: !cart.isOpen
    });
  },
  
  loadCart: () => {
    const savedCart = localStorage.getItem('cart');
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        
        // Validate and fix any potential issues with the loaded cart
        const validatedItems = (parsedCart.items || []).map(item => ({
          ...item,
          price: Number(item.price || 0),
          quantity: Number(item.quantity || 1)
        }));
        
        // Recalculate totals to ensure consistency
        const count = validatedItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const total = validatedItems.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0);
        
        setRecoil(cartState, {
          items: validatedItems,
          count: count,
          total: total,
          isOpen: false
        });

        console.log('Cart loaded from localStorage (initial):', {
          items: validatedItems,
          count,
          total
        });

        // Background refresh: attempt to fetch current product data for each item
        // and update prices to reflect any active promotions.
        (async () => {
          try {
            // Resolve BASEURL safely: prefer env variable but fall back to relative path
            let BASEURL = '';
            try {
              BASEURL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ? process.env.REACT_APP_API_URL : '';
            } catch (e) {
              BASEURL = '';
            }
            const refreshedItems = await Promise.all(validatedItems.map(async (item) => {
              try {
                if (!item.productId) return item;
                const res = await fetch(`${BASEURL}/Product/byId/${item.productId}`);
                if (!res.ok) return item;
                const prod = await res.json();
                const parseBool = (v) => v === true || v === 'true' || v === 1 || v === '1';
                const rawPrice = parseFloat(prod.prixVenteTTC ?? prod.prixVenteHt ?? prod.price ?? 0) || 0;
                const promoPriceVal = parseFloat(prod.prixVentePromo ?? prod.PrixVentePromo ?? prod.promoPrice ?? 0) || 0;
                const promoActive = parseBool(prod.enPromo ?? prod.EnPromo) || promoPriceVal > 0;
                const unitPrice = promoActive && promoPriceVal > 0 ? promoPriceVal : rawPrice;
                return {
                  ...item,
                  price: Number(unitPrice || 0),
                  enPromo: promoActive,
                  prixVentePromo: promoPriceVal,
                  rawPrice: rawPrice
                };
              } catch (e) {
                return item;
              }
            }));

            const refreshedCount = refreshedItems.reduce((s, it) => s + Number(it.quantity || 0), 0);
            const refreshedTotal = refreshedItems.reduce((s, it) => s + (Number(it.price || 0) * Number(it.quantity || 0)), 0);

            setRecoil(cartState, {
              ...getRecoil(cartState),
              items: refreshedItems,
              count: refreshedCount,
              total: refreshedTotal,
              isOpen: false
            });

            localStorage.setItem('cart', JSON.stringify({ items: refreshedItems, count: refreshedCount, total: refreshedTotal }));

            console.log('Cart refreshed with latest product data:', { items: refreshedItems, count: refreshedCount, total: refreshedTotal });
          } catch (err) {
            console.error('Error refreshing cart items:', err);
          }
        })();
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        // Reset to empty cart if there was an error
        setRecoil(cartState, {
          items: [],
          count: 0,
          total: 0,
          isOpen: false
        });
      }
    }
  }
};